import { STORY_SAMPLE_CATALOG } from './catalog.sample';
import type {
  StoryCatalog,
  StoryFrameAsset,
  StoryFrameStyle,
  StoryFrameStyleId,
  StoryLocale,
  StoryLocaleText,
  StoryPlayMode,
  StoryTemplate,
  StoryTemplateFrameAsset,
  StoryTemplateLayout,
} from './types';

export const STORY_BASE_PATH = '/three-sec-story';
export const STORY_CAPTURE_DURATION_MS = 5000;
export const STORY_EXPORT_WIDTH = 1080;
export const STORY_EXPORT_HEIGHT = 1920;
export const STORY_CAPTURE_WIDTH = 1920;
export const STORY_CAPTURE_HEIGHT = 1080;
export const STORY_CAPTURE_MIN_WIDTH = 1280;
export const STORY_CAPTURE_MIN_HEIGHT = 720;
export const STORY_CAPTURE_FRAME_RATE = 30;
export const STORY_CAMERA_RECORDER_BITS_PER_SECOND = 12_000_000;
export const STORY_EXPORT_RECORDER_BITS_PER_SECOND = 14_000_000;
export const STORY_NAVIGATION_EVENT = 'app:navigate';
export const STORY_RECORDER_MIME_TYPES = [
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
  'video/mp4',
] as const;
const STORY_FRAME_IMAGE_PROXY_PATH = '/api/story-frame-image';

export const STORY_FRAME_STYLES: StoryFrameStyle[] = [
  { id: 'none', name: '없음', colors: [], width: 0 },
  { id: 'white', name: '화이트', colors: ['#ffffff'], width: 6 },
  { id: 'rainbow', name: '레인보우', colors: ['#FF6B6B', '#FFD93D', '#6BCB77', '#4D96FF', '#FF6B6B', '#FFD93D'], width: 6 },
  { id: 'pastel', name: '파스텔', colors: ['#FFB5E8', '#B5DEFF', '#E7FFAC', '#FFC9DE', '#C4FAF8', '#FFE4A0'], width: 6 },
];

export const STORY_FALLBACK_CATALOG = STORY_SAMPLE_CATALOG;

const STORY_SUPPORTED_LOCALES: StoryLocale[] = ['ko', 'en', 'ja'];
const STORY_SUPPORTED_FRAME_STYLES: StoryFrameStyleId[] = ['none', 'white', 'rainbow', 'pastel'];
const STORY_SUPPORTED_PLAY_MODES: StoryPlayMode[] = ['simultaneous', 'sequential', 'sequential_reveal'];

// 순차 재생 계열(현재 sequential + sequential_reveal)은 총 길이/활성 인덱스 로직이 동일하다.
// 차이는 비활성 슬롯의 렌더 방식(썸네일 유지 vs 검정)과 이미 재생된 슬롯의 잔상 정책(첫 프레임 vs 마지막 프레임).
export const isStorySequentialPlayMode = (playMode: StoryPlayMode): boolean =>
  playMode === 'sequential' || playMode === 'sequential_reveal';

const getStoryFallbackLayout = (slotCount: number): StoryTemplateLayout => ({
  type: 'preset',
  preset: slotCount === 4 ? 'stack-4' : 'stack-3',
});

export const normalizeStoryLocale = (locale: string): StoryLocale => {
  const baseLocale = locale.toLowerCase().split('-')[0] as StoryLocale;
  return STORY_SUPPORTED_LOCALES.includes(baseLocale) ? baseLocale : 'ko';
};

export const resolveStoryLocaleText = (
  text: StoryLocaleText | null | undefined,
  locale: StoryLocale,
  fallback: string
): string => {
  const resolvedText = text?.[locale] ?? text?.ko ?? text?.en ?? text?.ja;
  return typeof resolvedText === 'string' && resolvedText.trim() !== ''
    ? resolvedText
    : fallback;
};

const resolveStoryFrameStyleId = (frameStyleId: StoryFrameStyleId | null | undefined): StoryFrameStyleId =>
  frameStyleId && STORY_SUPPORTED_FRAME_STYLES.includes(frameStyleId)
    ? frameStyleId
    : 'none';

const resolveStoryPlayMode = (playMode: StoryPlayMode | null | undefined): StoryPlayMode =>
  playMode && STORY_SUPPORTED_PLAY_MODES.includes(playMode)
    ? playMode
    : 'simultaneous';

const normalizeStoryStringArray = (values: unknown): string[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set<string>();
  return values.reduce<string[]>((result, value) => {
    if (typeof value !== 'string') {
      return result;
    }

    const normalizedValue = value.trim();
    if (!normalizedValue || seen.has(normalizedValue)) {
      return result;
    }

    seen.add(normalizedValue);
    result.push(normalizedValue);
    return result;
  }, []);
};

const resolveStoryFrameAssetImageUrl = (sourceUrl: string): string => {
  if (!/^https?:\/\//i.test(sourceUrl)) {
    return sourceUrl;
  }

  return `${STORY_FRAME_IMAGE_PROXY_PATH}?url=${encodeURIComponent(sourceUrl)}`;
};

const normalizeStoryFrameAsset = (
  asset: StoryFrameAsset,
  locale: StoryLocale
): StoryTemplateFrameAsset | null => {
  const id = typeof asset.id === 'string' ? asset.id.trim() : '';
  const sourceImageUrl = typeof asset.imageUrl === 'string' ? asset.imageUrl.trim() : '';
  if (!id || !sourceImageUrl || asset.enabled === false) {
    return null;
  }

  const sourceThumbnailUrl = typeof asset.thumbnailUrl === 'string' && asset.thumbnailUrl.trim() !== ''
    ? asset.thumbnailUrl.trim()
    : sourceImageUrl;

  return {
    id,
    name: resolveStoryLocaleText(asset.title, locale, id),
    imageUrl: resolveStoryFrameAssetImageUrl(sourceImageUrl),
    thumbnailUrl: resolveStoryFrameAssetImageUrl(sourceThumbnailUrl),
    sourceImageUrl,
    sourceThumbnailUrl,
    sortOrder: Number.isFinite(asset.sortOrder) ? asset.sortOrder : 0,
  };
};

const getStoryFrameAssetLookups = (
  catalog: StoryCatalog,
  locale: StoryLocale
): {
  byId: Map<string, StoryTemplateFrameAsset>;
  idsByTemplateId: Map<string, string[]>;
} => {
  const assets = Array.isArray(catalog.frameAssets) ? catalog.frameAssets : [];
  const normalizedAssets = assets
    .map((asset) => ({
      source: asset,
      normalized: normalizeStoryFrameAsset(asset, locale),
    }))
    .filter((entry): entry is { source: StoryFrameAsset; normalized: StoryTemplateFrameAsset } =>
      entry.normalized !== null
    )
    .sort((left, right) => left.normalized.sortOrder - right.normalized.sortOrder);

  const byId = new Map(normalizedAssets.map((entry) => [entry.normalized.id, entry.normalized]));
  const idsByTemplateId = new Map<string, string[]>();

  normalizedAssets.forEach(({ source, normalized }) => {
    normalizeStoryStringArray(source.templateIds).forEach((templateId) => {
      const currentIds = idsByTemplateId.get(templateId) ?? [];
      currentIds.push(normalized.id);
      idsByTemplateId.set(templateId, currentIds);
    });
  });

  return { byId, idsByTemplateId };
};

export const getStoryFrameStyleById = (frameStyleId: StoryFrameStyleId): StoryFrameStyle =>
  STORY_FRAME_STYLES.find((style) => style.id === frameStyleId) ?? STORY_FRAME_STYLES[0];

export const getStoryTemplates = (locale: string, catalog: StoryCatalog = STORY_FALLBACK_CATALOG): StoryTemplate[] => {
  const resolvedLocale = normalizeStoryLocale(locale);
  const frameAssetLookups = getStoryFrameAssetLookups(catalog, resolvedLocale);
  const enabledCategoryIds = new Set(
    catalog.categories
      .filter((category) => category.enabled)
      .map((category) => category.id)
  );

  return catalog.templates
    .filter((template) =>
      template.enabled
      && enabledCategoryIds.has(template.categoryId)
      && template.slotCount === template.slots.length
      && (template.slotCount === 3 || template.slotCount === 4)
    )
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((template) => {
      const frameAssetIds = normalizeStoryStringArray(template.frameAssetIds);
      const resolvedFrameAssetIds = frameAssetIds.length > 0
        ? frameAssetIds
        : (frameAssetLookups.idsByTemplateId.get(template.id) ?? []);
      const frameAssets = resolvedFrameAssetIds
        .map((frameAssetId) => frameAssetLookups.byId.get(frameAssetId))
        .filter((asset): asset is StoryTemplateFrameAsset => Boolean(asset));

      return {
        id: template.id,
        name: resolveStoryLocaleText(template.title, resolvedLocale, template.id),
        description: resolveStoryLocaleText(template.description, resolvedLocale, ''),
        labels: template.slots.map((slot, index) =>
          resolveStoryLocaleText(slot.label, resolvedLocale, `Slot ${index + 1}`)
        ),
        iconKey: template.iconKey?.trim() || '🎬',
        layout: template.layout ?? getStoryFallbackLayout(template.slotCount),
        defaultFrameStyleId: resolveStoryFrameStyleId(template.defaults?.frameStyleId),
        defaultPlayMode: resolveStoryPlayMode(template.defaults?.playMode),
        subscriptionRequired: template.subscriptionRequired === true,
        frameAssetIds: resolvedFrameAssetIds,
        frameAssets,
      };
    })
    .map((template) => ({
      ...template,
      description: template.description === '' ? null : template.description,
    }));
};

export const getStoryTemplateById = (
  templateId: string,
  locale: string,
  catalog: StoryCatalog = STORY_FALLBACK_CATALOG
): StoryTemplate | null =>
  getStoryTemplates(locale, catalog).find((template) => template.id === templateId) ?? null;
